'use client';

import { useCallback, useMemo } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useQuery } from '@tanstack/react-query';
import { useUserContext } from '@/contexts/UserProvider';
import { useWorkspace } from '@/contexts/WorkspaceProvider';
import { getWorkspaceUsers, type WorkspaceUser } from '@/services/gtmSignals';
import {
  applyViewAsParams,
  parseViewAsExperience,
  resolveRepExperience,
  VIEW_AS_EXPERIENCE_QUERY_PARAM,
  VIEW_AS_QUERY_PARAM,
  viewAsExperienceFor,
  type ViewAsExperience,
} from '@/constants/repExperience';

/** Why a requested `?viewAs=` target did not become a drill-in. */
export type ViewAsUnresolvedReason =
  /** Unknown, stale, malformed, or a member of another workspace. */
  | 'not-a-member'
  /** A real member of this workspace with no SDR or AE experience to open. */
  | 'no-rep-experience'
  /** The viewer may not drill into that particular experience. */
  | 'not-permitted';

/**
 * Every way a requested target can fail to open, including the roster request
 * itself failing. Surfaces that report the failure to the viewer take this.
 */
export type ViewAsFailure = ViewAsUnresolvedReason | 'roster-error';

interface ViewAsTargetShared {
  /** Workspace admins and super admins may open another rep's Today. */
  canViewAsSdr: boolean;
  /**
   * Super admins only. AE surfaces carry CRM deal amounts and prepared drafts,
   * and the server enforces the same super-admin check on `?viewAsUserId=`.
   */
  canViewAsAe: boolean;
  /** The whole workspace roster, for pickers to filter as they see fit. */
  roster: WorkspaceUser[];
  isRosterLoading: boolean;
  isRosterError: boolean;
  retryRoster: () => void;
  /**
   * The experience the URL claims to be opening. A hint for choosing a skeleton
   * while the roster loads — the resolved member overrides it, always.
   */
  declaredExperience: ViewAsExperience | null;
  /** Write (or clear, with null) the view-as params on the current route. */
  setViewAs: (userId: string | null, experience?: ViewAsExperience | null) => void;
}

type ViewAsTargetVariant =
  /** Own view: no target requested, or the viewer asked for themselves. */
  | { status: 'none'; requestedUserId: null; experience: null; user: null; reason: null }
  /** Roles or roster still in flight. Render nothing experience-specific yet. */
  | { status: 'resolving'; requestedUserId: string; experience: null; user: null; reason: null }
  /** The roster could not be loaded, so the target is unknowable right now. */
  | { status: 'error'; requestedUserId: string; experience: null; user: null; reason: null }
  | { status: 'unresolved'; requestedUserId: string; experience: null; user: null; reason: ViewAsUnresolvedReason }
  | { status: 'resolved'; requestedUserId: string; experience: ViewAsExperience; user: WorkspaceUser; reason: null };

export type ViewAsTargetState = ViewAsTargetShared & ViewAsTargetVariant;

/**
 * Resolve the `?viewAs=<uuid>` target of the current route against the active
 * workspace, and decide which rep experience that member actually has.
 *
 * This is the single resolution point behind Today, Actions, the sidebar and
 * the view-as pickers, so those surfaces cannot disagree about who is being
 * viewed or what they are. Three properties matter and are covered by tests:
 *
 *  1. The parameter never implies an experience. `resolveRepExperience` reads
 *     the member's own `ui_persona`, and explicit configuration wins over CRM
 *     deal ownership — an SDR who owns opportunities stays an SDR.
 *  2. Nothing experience-specific renders while roles or the roster are still
 *     loading: the state is 'resolving', not a guess.
 *  3. An id that is unknown, stale, malformed or from another workspace lands
 *     on 'unresolved' and never becomes a data fetch. Every underlying endpoint
 *     is vendor-scoped and re-checks authorization server side; this hook only
 *     decides which member of the *current* workspace is asked for.
 */
export interface UseViewAsTargetOptions {
  /**
   * Fetch the roster even when no target is requested. Only the pickers need
   * that — they have to offer someone before a target exists. Everything else
   * (Today, Actions, the sidebar) leaves it off, so an admin browsing unrelated
   * pages does not pull a roster nothing on screen will use.
   */
  rosterForPicker?: boolean;
}

export function useViewAsTarget(options: UseViewAsTargetOptions = {}): ViewAsTargetState {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const workspaceContext = useWorkspace();
  const { askIOTUserDetails } = useUserContext();

  const vendorId = workspaceContext?.activeWorkspace?.vendorid;
  const currentUserId = askIOTUserDetails?.user_id;
  const isSuperAdmin = workspaceContext?.isSuperAdmin === true;
  const isAdmin = workspaceContext?.isAdmin === true;
  // `isSuperAdmin` reads false while the workspaces query is still disabled —
  // "not known yet", not "not allowed". Denying a drill-in on that would show
  // the viewer their own dashboard under someone else's URL for a frame.
  const isRoleResolved = workspaceContext?.isRoleResolved !== false;

  const canViewAsSdr = isSuperAdmin || isAdmin;
  const canViewAsAe = isSuperAdmin;
  const canViewAsAnyone = canViewAsSdr || canViewAsAe;

  const rawRequestedUserId = searchParams?.get(VIEW_AS_QUERY_PARAM)?.trim() || null;
  const declaredExperience = parseViewAsExperience(
    searchParams?.get(VIEW_AS_EXPERIENCE_QUERY_PARAM),
  );
  // Viewing yourself is your own view, not a drill-in.
  const requestedUserId = rawRequestedUserId && rawRequestedUserId !== currentUserId
    ? rawRequestedUserId
    : null;

  const rosterQuery = useQuery({
    // One shared key, so the several consumers on a page make one request.
    queryKey: ['workspace-users', vendorId],
    queryFn: () => getWorkspaceUsers(vendorId as number),
    enabled: (Boolean(requestedUserId) || options.rosterForPicker === true)
      && canViewAsAnyone && isRoleResolved && !!vendorId,
    staleTime: 5 * 60 * 1000,
    // An initial failure has no cached data. Retrying when a child subscribes
    // would make it pending again, unmount that child, and repeat indefinitely.
    // Recovery is explicit through retryRoster, not observer mount churn.
    retryOnMount: false,
  });

  const roster = useMemo(() => rosterQuery.data?.users ?? [], [rosterQuery.data]);

  const setViewAs = useCallback((userId: string | null, experience?: ViewAsExperience | null) => {
    const params = applyViewAsParams(
      new URLSearchParams(searchParams?.toString() ?? ''),
      userId,
      experience ?? null,
    );
    const queryString = params.toString();
    router.replace(queryString ? `${pathname}?${queryString}` : pathname, { scroll: false });
  }, [pathname, router, searchParams]);

  const { refetch: refetchRoster } = rosterQuery;
  const retryRoster = useCallback(() => { void refetchRoster(); }, [refetchRoster]);

  const shared: ViewAsTargetShared = {
    canViewAsSdr,
    canViewAsAe,
    roster,
    isRosterLoading: rosterQuery.isLoading,
    isRosterError: rosterQuery.isError,
    retryRoster,
    declaredExperience,
    setViewAs,
  };

  const isRosterPending = rosterQuery.isPending;
  const isRosterError = rosterQuery.isError;
  // Keep mounted query observers stable during background refreshes. Gating on
  // isFetching unmounts the very children that subscribe to this roster, which
  // can repeatedly refetch on remount (especially after an error/429).
  // Cached personas may briefly lag a change until the normal refresh settles;
  // backend eligibility checks remain authoritative for AE data access.

  const variant = useMemo<ViewAsTargetVariant>(() => {
    const ownView = {
      status: 'none', requestedUserId: null, experience: null, user: null, reason: null,
    } as const;

    if (!requestedUserId) return ownView;

    // A plain member's hand-crafted link is ignored outright — exactly as it was
    // before this hook existed. They simply get their own dashboard.
    if (isRoleResolved && !canViewAsAnyone) return ownView;

    // Roles, the signed-in identity or the active workspace have not answered
    // yet. `currentUserId` matters here too: without it a link a viewer opened
    // on *themselves* looks like a drill-in for a frame. TanStack v5 reports a
    // disabled query as pending but not loading, so gate on both rather than
    // trusting `isLoading` alone.
    if (!isRoleResolved || !currentUserId || !vendorId || isRosterPending) {
      return { status: 'resolving', requestedUserId, experience: null, user: null, reason: null };
    }

    if (isRosterError) {
      return { status: 'error', requestedUserId, experience: null, user: null, reason: null };
    }

    const member = roster.find((user) => user.user_id === requestedUserId);
    if (!member) {
      return { status: 'unresolved', requestedUserId, experience: null, user: null, reason: 'not-a-member' };
    }

    const experience = viewAsExperienceFor(member);
    if (!experience) {
      return { status: 'unresolved', requestedUserId, experience: null, user: null, reason: 'no-rep-experience' };
    }

    const permitted = experience === 'ae' ? canViewAsAe : canViewAsSdr;
    if (!permitted) {
      return { status: 'unresolved', requestedUserId, experience: null, user: null, reason: 'not-permitted' };
    }

    // `declaredExperience` is deliberately not consulted here. The member's own
    // configuration decides, so a link claiming `viewAsExperience=ae` about an
    // SDR opens that SDR's Today rather than misreading them as an AE.
    return { status: 'resolved', requestedUserId, experience, user: member, reason: null };
  }, [
    requestedUserId, isRoleResolved, currentUserId, canViewAsAnyone, canViewAsAe,
    canViewAsSdr, vendorId, isRosterPending, isRosterError, roster,
  ]);

  return { ...shared, ...variant };
}

/**
 * The picker half every "view as" surface shares: the roster members of one
 * experience other than yourself, sorted by how that surface names them, and a
 * setter that records which experience the pick meant to open. Who may pick,
 * and what an unaccepted target means, differ on purpose per surface and stay
 * in `useViewAsSdr` / `useViewAsAe`.
 */
export function useViewAsRoster(
  target: Pick<ViewAsTargetState, 'roster' | 'setViewAs'>,
  experience: ViewAsExperience,
  displayName: (user: WorkspaceUser) => string,
): { users: WorkspaceUser[]; setViewAsUserId: (userId: string | null) => void } {
  const { askIOTUserDetails } = useUserContext();
  const currentUserId = askIOTUserDetails?.user_id;
  const { roster, setViewAs } = target;

  const users = useMemo(() => roster
    .filter((user) => user.user_id !== currentUserId && resolveRepExperience(user) === experience)
    .sort((a, b) => displayName(a).localeCompare(displayName(b))),
  [roster, currentUserId, experience, displayName]);

  const setViewAsUserId = useCallback(
    (userId: string | null) => setViewAs(userId, userId ? experience : null),
    [setViewAs, experience],
  );

  return { users, setViewAsUserId };
}
